/**
 * Contact Edit Dialog Component
 *
 * Material Dialog for editing contact data
 */

import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CountryService } from '../../core/services/country.service';
import { TranslatePipe } from '../../core/features/translations';

export interface ContactEditDialogData {
  contact: any;
  countries: any[];
}

export interface ContactEditDialogResult {
  formValue: any;
}

@Component({
  selector: 'app-contact-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    TranslatePipe,
  ],
  template: `
    <h2 mat-dialog-title>{{ 'portal.mydata.contact.dialog.title' | translate }}</h2>
    <mat-dialog-content>
      <form [formGroup]="contactForm" class="edit-form">
        <!-- Personal Information Section -->
        <div class="form-section">
          <h3 class="section-title">{{ 'portal.mydata.contact.dialog.personal' | translate }}</h3>

          <div class="form-grid">
            <!-- Gender -->
            <mat-form-field appearance="outline">
              <mat-label>{{ 'portal.mydata.contact.dialog.salutation' | translate }}</mat-label>
              <mat-select formControlName="gender">
                <mat-option value="">{{
                  'portal.mydata.contact.dialog.select' | translate
                }}</mat-option>
                <mat-option value="M">{{
                  'portal.mydata.contact.dialog.mr' | translate
                }}</mat-option>
                <mat-option value="F">{{
                  'portal.mydata.contact.dialog.mrs' | translate
                }}</mat-option>
                <mat-option value="N">{{
                  'portal.mydata.contact.dialog.diverse' | translate
                }}</mat-option>
              </mat-select>
            </mat-form-field>

            <!-- First Name -->
            <mat-form-field appearance="outline">
              <mat-label>{{ 'portal.mydata.contact.dialog.firstname' | translate }} *</mat-label>
              <input matInput formControlName="prename" required />
              @if (contactForm.get('prename')?.hasError('required') &&
              contactForm.get('prename')?.touched) {
              <mat-error>{{
                'portal.mydata.contact.dialog.firstnameRequired' | translate
              }}</mat-error>
              }
            </mat-form-field>

            <!-- Last Name -->
            <mat-form-field appearance="outline">
              <mat-label>{{ 'portal.mydata.contact.dialog.lastname' | translate }} *</mat-label>
              <input matInput formControlName="lastname" required />
              @if (contactForm.get('lastname')?.hasError('required') &&
              contactForm.get('lastname')?.touched) {
              <mat-error>{{
                'portal.mydata.contact.dialog.lastnameRequired' | translate
              }}</mat-error>
              }
            </mat-form-field>

            <!-- Company Name -->
            <mat-form-field appearance="outline">
              <mat-label>{{ 'portal.mydata.contact.dialog.company' | translate }}</mat-label>
              <input matInput formControlName="companyname" />
            </mat-form-field>

            <!-- Birthday -->
            <mat-form-field appearance="outline">
              <mat-label>{{ 'portal.mydata.contact.dialog.birthday' | translate }}</mat-label>
              <input matInput type="date" formControlName="birthday" />
            </mat-form-field>

            <!-- Nationality -->
            <mat-form-field appearance="outline">
              <mat-label>{{ 'portal.mydata.contact.dialog.nationality' | translate }}</mat-label>
              <mat-select formControlName="nationality">
                <mat-option [value]="null">{{
                  'portal.mydata.contact.dialog.select' | translate
                }}</mat-option>
                @for (country of data.countries; track country.country_id) {
                <mat-option [value]="country.country_id">{{ country.country_name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <!-- Hometown -->
            <mat-form-field appearance="outline">
              <mat-label>{{ 'portal.mydata.contact.dialog.hometown' | translate }}</mat-label>
              <input matInput formControlName="hometown" />
            </mat-form-field>

            <!-- Phone -->
            <mat-form-field appearance="outline">
              <mat-label>{{ 'portal.mydata.contact.dialog.phone' | translate }}</mat-label>
              <input matInput type="tel" formControlName="phone" />
            </mat-form-field>

            <!-- AHV Number -->
            <mat-form-field appearance="outline">
              <mat-label>{{ 'portal.mydata.contact.dialog.ahv' | translate }}</mat-label>
              <input matInput formControlName="ahv_number" />
            </mat-form-field>
          </div>
        </div>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">
        {{ 'portal.mydata.contact.dialog.cancel' | translate }}
      </button>
      <button
        mat-raised-button
        color="primary"
        (click)="onSave()"
        [disabled]="contactForm.invalid || saving()"
      >
        @if (saving()) {
        <span>{{ 'portal.mydata.contact.dialog.saving' | translate }}</span>
        } @else {
        <span>{{ 'portal.mydata.contact.dialog.save' | translate }}</span>
        }
      </button>
    </mat-dialog-actions>
  `,
  styles: `
    mat-dialog-content {
      min-width: 1000px;
      max-height: 80vh;
      overflow-y: auto;
      padding: 24px;
    }

    .edit-form {
      padding: 16px 0;
    }

    .form-section {
      margin-bottom: 24px;
    }

    .section-title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 16px;
      color: #333;
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
    }

    mat-form-field {
      width: 100%;
    }

    mat-dialog-actions {
      padding: 16px 24px;
    }
  `,
})
export class ContactEditDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<ContactEditDialogComponent>);
  protected readonly data: ContactEditDialogData = inject(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);

  readonly saving = signal(false);
  contactForm!: FormGroup;

  constructor() {
    this.initializeForm();
    this.patchFormValues(this.data.contact);
  }

  private initializeForm(): void {
    this.contactForm = this.fb.group({
      prename: ['', Validators.required],
      lastname: ['', Validators.required],
      companyname: [''],
      gender: [''],
      birthday: [''],
      nationality: [''],
      hometown: [''],
      phone: [''],
      ahv_number: [''],
    });
  }

  private patchFormValues(contact: any): void {
    this.contactForm.patchValue({
      prename: contact.prename || '',
      lastname: contact.lastname || '',
      companyname: contact.companyname || '',
      gender: contact.gender || '',
      birthday: contact.birthday || '',
      nationality: contact.nationality || '',
      hometown: contact.hometown || '',
      phone: contact.phone || '',
      ahv_number: contact.ahv || '',
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    const result: ContactEditDialogResult = {
      formValue: this.contactForm.value,
    };

    this.dialogRef.close(result);
  }
}
